// Persists the unsent assistant-chat input text across a host-page reload.
// Neptune's ticket system hard-navigates every ~5 min, tearing down the
// content-script iframe and every in-memory React state with it — so anything
// the user had typed but not yet sent is lost. This store mirrors the message
// cache discipline (see neptune useMessageCache): versioned, TTL'd, one key per
// chat context, bounded entry count, quota-safe, and window.localStorage (not
// sessionStorage) so a draft also survives a tab close/reopen.

const DRAFT_VERSION = 2;
const DRAFT_PREFIX = "lytra_assist_draft_v2:";
// Matches the 30-min TTL of the message cache: a draft older than this is stale
// enough that silently dropping it is preferable to resurrecting forgotten text.
const DRAFT_TTL_MS = 30 * 60 * 1000;
// One draft per active chat context (ticket / global). A handful of stale keys
// is the worst case; cap well below the localStorage budget regardless.
const MAX_DRAFTS = 20;

interface PersistedDraft {
  version: number;
  timestamp: number;
  text: string;
}

/** localStorage access that never throws (Safari private mode, disabled storage, SSR). */
function safeLocalStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Every persisted draft key with its timestamp, oldest first. Drops any key
 * whose payload is corrupt or from an older schema version as a side effect.
 */
function listDrafts(store: Storage): { key: string; timestamp: number }[] {
  const out: { key: string; timestamp: number }[] = [];
  for (let i = 0; i < store.length; i++) {
    const key = store.key(i);
    if (!key || !key.startsWith(DRAFT_PREFIX)) continue;
    try {
      const parsed = JSON.parse(store.getItem(key) ?? "") as PersistedDraft;
      if (parsed?.version !== DRAFT_VERSION) {
        store.removeItem(key); // stale schema — drop it
        continue;
      }
      out.push({ key, timestamp: parsed.timestamp ?? 0 });
    } catch {
      store.removeItem(key); // corrupt — drop it
    }
  }
  return out.sort((a, b) => a.timestamp - b.timestamp);
}

/**
 * The persisted draft for a chat context, or "" when there is nothing safe to
 * restore (no key, expired, wrong version, corrupt). Used to repopulate the
 * input on mount so a host-page hard refresh doesn't discard in-progress text.
 */
export function loadAssistantDraft(draftKey: string): string {
  const store = safeLocalStorage();
  if (!store || !draftKey) return "";
  const key = DRAFT_PREFIX + draftKey;
  try {
    const raw = store.getItem(key);
    if (!raw) return "";
    const parsed = JSON.parse(raw) as PersistedDraft;
    if (parsed.version !== DRAFT_VERSION) {
      store.removeItem(key);
      return "";
    }
    if (Date.now() - parsed.timestamp > DRAFT_TTL_MS) {
      store.removeItem(key);
      return "";
    }
    return typeof parsed.text === "string" ? parsed.text : "";
  } catch {
    store.removeItem(key);
    return "";
  }
}

/**
 * Persist (or, when `text` is empty, drop) the draft for a chat context. Called
 * on every keystroke so an abrupt hard navigation can't lose the last few
 * characters — debouncing would.
 */
export function saveAssistantDraft(draftKey: string, text: string): void {
  const store = safeLocalStorage();
  if (!store || !draftKey) return;
  const key = DRAFT_PREFIX + draftKey;
  // Empty draft -> remove the key rather than persist an empty entry.
  if (!text) {
    store.removeItem(key);
    return;
  }
  const payload = JSON.stringify({
    version: DRAFT_VERSION,
    timestamp: Date.now(),
    text,
  } satisfies PersistedDraft);

  const write = (): boolean => {
    try {
      store.setItem(key, payload);
      return true;
    } catch {
      return false; // quota exceeded or storage disabled
    }
  };

  if (!write()) {
    // Evict the oldest draft (never the one we're writing) and retry once.
    const others = listDrafts(store).filter((e) => e.key !== key);
    if (others[0]) store.removeItem(others[0].key);
    if (!write()) return;
  }

  // Enforce the count cap, oldest first. The just-written entry carries the
  // newest timestamp, so it is never the eviction target.
  const drafts = listDrafts(store);
  for (let i = 0; i < drafts.length - MAX_DRAFTS; i++) {
    store.removeItem(drafts[i].key);
  }
}

/** Drop the draft for a chat context (call after a successful send). */
export function clearAssistantDraft(draftKey: string): void {
  if (!draftKey) return;
  safeLocalStorage()?.removeItem(DRAFT_PREFIX + draftKey);
}

/** Drop every persisted draft (call on logout — drafts can hold customer PII). */
export function clearAllAssistantDrafts(): void {
  const store = safeLocalStorage();
  if (!store) return;
  const keys: string[] = [];
  for (let i = 0; i < store.length; i++) {
    const key = store.key(i);
    if (key && key.startsWith(DRAFT_PREFIX)) keys.push(key);
  }
  for (const key of keys) store.removeItem(key);
}
